import React from 'react';

import { ThemeProvider, styled } from 'styled-components';
import { theme } from '../theme/aikya';

import { Header }  from './Header';
import { HeroParagraph, Paragraph, ParagraphSmall, UnorderedList, UnorderedListItem } from '../theme/typography';
import { Button  } from './Button';
import { TextLink } from './TextLink';
import { PageTitle } from './PageTitle';
import { StandardSectionContentContainer, StandardSection, StandardSectionContentBlock } from './StandardSection';
import { ContactBlock } from './ContactBlock';
import { ServalActionItemBlock } from './ServalActionItemBlock';
import { Breadcrumbs, BreadcrumbChevron, BreadcrumbLink } from './Breadcrumbs';
import { PageWrapper } from './PageWrapper';
import { FooterSection } from './FooterSection';
import { ContributorRow } from "./ContributorRow";
import { useContributorsByRole } from "../hooks/useContributorsByRole";
import { SupportBlock } from './SupportBlock';

import HeroJokerImage from '../images/parts/hero-joker.png';
import CamAnglesImage from '../images/parts/joker cam angles.png';
import CamVariantsImage from '../images/parts/joker variants.png';

const ButtonContainer = styled.div`
	align-items: stretch;
	margin-bottom: 2rem;
	margin-top: 2rem;
	max-width: 32rem;
	display: flex;
	gap: 1rem;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.first}) {
		display: flex;
	}
`;

const StyledHeroImage = styled.img`
	border-radius: 0.5rem;
	width: 100%;
`;

const TextContainer = styled.div`
	max-width: 32rem;
`;

const TopContainer = styled.div`
	max-width: 36rem;
`;

const FeatureListItem = styled(UnorderedListItem)`
	${Paragraph} {
		margin-bottom: 0.25rem;
	}

	${ParagraphSmall} {
		color: ${(props) => props.theme.colors.neutral4};
		margin-top: 0.25rem;
	}
`;

const Heading = styled.h3`
	${(props) => props.theme.typography.headingS};
	margin-bottom: 1rem;
`;

const Contributors = styled.div`
`;

export function JokerPage() {
	const contributorData = useContributorsByRole("joker");

	return (
			<ThemeProvider theme={theme}>
				<PageWrapper>
				<Header />
					<StandardSection>
						<StandardSectionContentContainer>
							<TopContainer>
								<Breadcrumbs>
									<BreadcrumbLink to="/parts">
										Parts
									</BreadcrumbLink>
									<BreadcrumbChevron>&gt;</BreadcrumbChevron>
								</Breadcrumbs>
								<PageTitle>Joker</PageTitle>
								<HeroParagraph>An universal ultralight tinywhoop racing canopy offering great camera and FC protection for as low as 0.76 grams of weight.</HeroParagraph>
								<Contributors>
									{contributorData && contributorData.roleOrder.map(role => {
										if (!contributorData.contributorsByRole[role]) return null;
										
										return (
										<ContributorRow 
												key={role}
												roleLabel={contributorData.roleLabels[role]}
												contributorIds={contributorData.contributorsByRole[role]}
											/>
										);
									})}
								</Contributors>
								<ButtonContainer>
									<Button 
										as="a"
										colorContext="light"
										href={`https://www.printables.com/model/1425736-tressym-joker-racing-canopy-for-tinywhoops`}
										label="Download from Printables"
										download
									/>
									<Button 
										as="a"
										colorContext="light"
										href={`https://makerworld.com/en/models/1141378`}
										label="Download from Makerworld"
										download
										variant="outlined"
									/>
								</ButtonContainer>
							</TopContainer>
							<StyledHeroImage src={HeroJokerImage} alt="Joker hero image" />
							<TextContainer>
								<StandardSectionContentBlock>
									<Heading>Camera angle options</Heading>
									<Paragraph>The Joker supports camera angles from 10 degrees all the way to 50 degrees:</Paragraph>
									<StyledHeroImage src={CamAnglesImage} alt="Joker camera angles" />
									<Paragraph>The lowest camera angles need extra weight. If you want to reduce weight, use a camera angle of 35 degrees and above, and snip off the excessive part of the arms.</Paragraph>
								</StandardSectionContentBlock>
								<StandardSectionContentBlock>
									<Heading>Camera variants</Heading>
									<Paragraph>The Joker supports the following cameras:</Paragraph>
									<UnorderedList>
										<FeatureListItem>
											<Paragraph>Betafpv C03</Paragraph>
										</FeatureListItem>
										<FeatureListItem>
											<Paragraph>Betafpv P1</Paragraph>
										</FeatureListItem>
										<FeatureListItem>
											<Paragraph>Caddx Ant Lite</Paragraph>
										</FeatureListItem>
										<FeatureListItem>
											<Paragraph>DJI O4 Lite</Paragraph>
										</FeatureListItem>
										<FeatureListItem>
											<Paragraph>DJI O4 Lite (with O3 lens mod)</Paragraph>
										</FeatureListItem>
										<FeatureListItem>
											<Paragraph>HDZero Eco</Paragraph>
										</FeatureListItem>
										<FeatureListItem>
											<Paragraph>HDZero Lux</Paragraph>
										</FeatureListItem>
										<FeatureListItem>
											<Paragraph>Newbeedrone BeeEye v1</Paragraph>
										</FeatureListItem>
										<FeatureListItem>
											<Paragraph>Tinywhoop Pinch</Paragraph>
										</FeatureListItem>
									</UnorderedList>
									<Paragraph>You can identify camera variants based on the little notches on their center leg:</Paragraph>
									<StyledHeroImage src={CamVariantsImage} alt="Joker camera variants" />
									<Paragraph>The DJI O4 variants looks super different, so they're really easy to tell apart.</Paragraph>
									<Paragraph>For the HDZero Eco, put on the Joker and guide the loop between the wires <em>before</em> soldering on the camera.</Paragraph>
								</StandardSectionContentBlock>
								<Heading>Story</Heading>
								<Paragraph>I was greatly impressed by the efficiency of <TextLink link="https://newbeedrone.com/products/hummingbird-racespec-cam-mount-tpu-3d-printed-version" target="_blank" label="Newbeedrone's RaceSpec cam mount"></TextLink> - an excellent piece of engineering! I decided to try to further improve on this already great design, and I believe I might have succeeded:</Paragraph>
								<UnorderedList>
									<FeatureListItem>
										<Paragraph>Reduced weight</Paragraph>
										<ParagraphSmall>The Joker's weight is a tiny bit smaller, at just 0.76 grams.</ParagraphSmall>
									</FeatureListItem>
									<FeatureListItem>
										<Paragraph>Increased camera protection</Paragraph>
										<ParagraphSmall>The four spikes extending beyond the lens offer better protection to the camera in crashes.</ParagraphSmall>
									</FeatureListItem>
									<FeatureListItem>
										<Paragraph>Much easier disassembly</Paragraph>
										<ParagraphSmall>I found NBD's cam mount to be very hard to remove without tugging uncomfortably hard on the camera lens, or cutting the cam mount apart. The Joker can be easily removed when needed.</ParagraphSmall>
									</FeatureListItem>
									<FeatureListItem>
										<Paragraph>Better fit</Paragraph>
										<ParagraphSmall>Most tinywhoop cameras have a rectangular lens base. The Joker adheres to its shape perfectly.</ParagraphSmall>
									</FeatureListItem>
									<FeatureListItem>
										<Paragraph>Better printability</Paragraph>
										<ParagraphSmall>The Joker is perfectly sized for a 0.4mm printing nozzle, avoiding ambiguity in printing.</ParagraphSmall>
									</FeatureListItem>
									<FeatureListItem>
										<Paragraph>More cameras supported</Paragraph>
										<ParagraphSmall>The Joker supports more cameras, not just the BeeEye.</ParagraphSmall>
									</FeatureListItem>
									<FeatureListItem>
										<Paragraph>Cleaner arcs</Paragraph>
										<ParagraphSmall>In my opinion, the Joker's lines are a tiny bit neater. 🙂</ParagraphSmall>
									</FeatureListItem>
								</UnorderedList>
								<Paragraph>I retained the forward camera position, which I think is one of the smartest choices in NBD's design. It prevents the camera from impacting the flight controller in crashes.</Paragraph>
								<Paragraph>The Joker is open source. Feel free to use or remix it, just link back to this page when you do! I'm releasing it under the <TextLink link="https://creativecommons.org/licenses/by-sa/4.0/" label="Creative Commons-Attribution-Share Alike" /> license. You can find the <TextLink link="https://cad.onshape.com/documents/1f8c499a1c1d9277da81e70f/w/eb7ddaca4a4e98f36d862f6f/e/290860d471a04dd3c36465f0?renderMode=0&uiState=6932ef42426899ee58c84d56" label="editable Joker files" /> on Onshape.</Paragraph>
								<ButtonContainer>
									<Button 
										as="a"
										colorContext="light"
										href={`https://www.printables.com/model/1425736-tressym-joker-racing-canopy-for-tinywhoops`}
										label="Download from Printables"
										download
									/>
									<Button 
										as="a"
										colorContext="light"
										href={`https://makerworld.com/en/models/1141378`}
										label="Download from Makerworld"
										download
										variant="outlined"
									/>
								</ButtonContainer>
							</TextContainer>
						</StandardSectionContentContainer>
					</StandardSection>
					<ServalActionItemBlock />
					<SupportBlock />
					<ContactBlock />
					<FooterSection />
				</PageWrapper>
			</ThemeProvider>
		);
}